import { useCallback, useEffect, useRef, useState } from "react";
import { BRAND } from "../data/content";
import { cn } from "../lib/utils";

/**
 * Cookly brand wordmark (plate-rim mark lives only in favicon / SVG assets).
 * Single text node "Cookly" so metallic shimmer (background-clip:text) sweeps
 * continuously across letter glyphs only. Playfair italic; lemon ball accent
 * lives on the lockup (outside the clipped text). Hover shine yoyos while
 * hovered (`is-shimmering`); mouseleave removes the class to stop the loop.
 *
 * Accessible name: visible "Cookly" text (never aria-hidden). Parent <Link>
 * may add aria-label for Home purpose; do not hide the wordmark from AT.
 * Under prefers-reduced-motion: solid wordmark, no load/hover shimmer.
 */
export default function BrandLogo({
  className = "",
  wordClassName = "",
  title = BRAND.name,
  /** Lemon ball lockup accent — keep for Cookly wordmark; off for “By DLPM”. */
  accent = true,
  /** Load-in shimmer once; footer signature uses hover-only. */
  boot = true,
}) {
  const shimmerRef = useRef(null);
  const [reduceMotion, setReduceMotion] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false
  );

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduceMotion(media.matches);
    onChange();
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  const startShineLoop = useCallback(() => {
    if (reduceMotion) return;
    const el = shimmerRef.current;
    if (!el) return;
    el.classList.remove("is-shimmering");
    void el.offsetWidth;
    el.classList.add("is-shimmering");
  }, [reduceMotion]);

  const stopShineLoop = useCallback(() => {
    const el = shimmerRef.current;
    if (!el) return;
    el.classList.remove("is-shimmering");
  }, []);

  useEffect(() => {
    if (!boot) return undefined;
    const el = shimmerRef.current;
    if (!el || reduceMotion) return undefined;
    el.classList.add("brand-shimmer--boot");
    const onEnd = (event) => {
      if (event.target !== el) return;
      if (event.animationName === "brand-shimmer-load") {
        el.classList.remove("brand-shimmer--boot");
      }
    };
    el.addEventListener("animationend", onEnd);
    return () => el.removeEventListener("animationend", onEnd);
  }, [boot, reduceMotion]);

  return (
    <span
      className={cn(
        "group/logo inline-flex items-center text-navy",
        className,
      )}
      onMouseEnter={startShineLoop}
      onMouseLeave={stopShineLoop}
    >
      <span
        className={cn(
          "brand-lockup",
          accent && "brand-lockup--cookly",
        )}
      >
        <span
          ref={shimmerRef}
          className={cn(
            "brand-shimmer leading-none tracking-tight text-[26px] md:text-[30px]",
            reduceMotion && "brand-shimmer--static",
            wordClassName,
          )}
        >
          {title}
        </span>
      </span>
    </span>
  );
}
